import { useMutation } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";

import { useSetExtendedField, useUploadFile } from "ExtendedFields/API/queries";

import appendToFormData from "Utilities/appendToFormData";
import { generateUUID } from "Utilities/generateUUID";
import makeItAnArray from "Utilities/makeItAnArray";
import validateFileSize from "Utilities/validateFileSize";

import { clearExtendedFieldResponse } from "./fetch";

function useHandleUploadFile({ targetUserID, networkFieldID, file }) {
  const {
    mutateAsync: uploadFile,
    isLoading: isUploading,
    progressPercentage,
    ...rest
  } = useUploadFile();

  const [errorMessage, setErrorMessage] = useState(null);
  const clearUploadErrorMessage = () => {
    if (errorMessage) setErrorMessage(null);
  };

  const handleFileUpload = useCallback(
    async (fileArg = file) => {
      setErrorMessage("");
      if (!fileArg) {
        setErrorMessage("Please select a file to upload.");
        return;
      }
      const isValidSize = validateFileSize(fileArg.size);
      if (!isValidSize) {
        setErrorMessage("File size is too large!");
        return;
      }

      const formData = new FormData();
      formData.append("qquuid", generateUUID());
      formData.append("qqfilename", fileArg.name);
      formData.append("qqtotalfilesize", fileArg.size);
      formData.append("qqfile", fileArg);
      formData.append("networkAvailableFieldID", networkFieldID);
      formData.append("targetUserID", targetUserID);

      try {
        const response = await uploadFile(formData);
        return response;
      } catch (error) {
        setErrorMessage("There was an error uploading the file.");
      }
    },
    [file, networkFieldID, targetUserID, uploadFile]
  );

  return {
    handleFileUpload,
    isUploading,
    errorMessage,
    clearUploadErrorMessage,
    progressPercentage, // Explicitly exporting this for clarity
    ...rest,
  };
}

/**
 * @typedef {object} SaveFieldExtraProps
 * @property {object|string} newValue
 * @property {number} useID
 */

/** @param {ExtendedField & SaveFieldExtraProps} param0  */
const useSaveField = ({
  newValue,
  userID,
  type,
  networkFieldID,
  min,
  max,
  onSuccess,
  rangeContext,
}) => {
  const [validationError, setValidationError] = useState("");
  const {
    handleFileUpload,
    progressPercentage,
    errorMessage: fileErrorMessage,
    clearUploadErrorMessage,
    isUploading,
  } = useHandleUploadFile({
    targetUserID: userID,
    networkFieldID,
    file: newValue,
  });
  const {
    mutateAsync,
    errorMessage: standardErrorMessage,
    clearErrorMessage: clearStandardErrorMessage,
    isLoading,
  } = useSetExtendedField(onSuccess);

  const errorMessage = useMemo(() => {
    if (validationError) return validationError;
    if (type === "file") return fileErrorMessage;
    return standardErrorMessage;
  }, [standardErrorMessage, fileErrorMessage, type, validationError]);

  const clearErrorMessage = useCallback(() => {
    clearStandardErrorMessage();
    clearUploadErrorMessage();
    setValidationError("");
  }, [clearStandardErrorMessage, clearUploadErrorMessage]);

  const saveField = useCallback(async () => {
    const formData = new FormData();
    formData.append("networkFieldID", networkFieldID);
    switch (type) {
      case "address":
      case "date":
        appendToFormData(formData, newValue);
        break;
      case "membershipstatus":
      case "picker": {
        const values = makeItAnArray(newValue);

        appendToFormData(formData, {
          values: values.map(({ label, id }) => ({
            optionListValue: label,
            optionListValueID: id,
          })),
        });
        break;
      }
      case "file":
        return await handleFileUpload();
      case "number":
        if (!rangeContext || rangeContext !== "year") {
          // do standard number validation
          if (Number.isInteger(min) && Number(newValue) < min) {
            setValidationError(`Must be at least ${min}.`);
            return { success: false };
          }
          if (Number.isInteger(max) && Number(newValue) > max) {
            setValidationError(`Must be no greater than ${max}.`);
            return { success: false };
          }
        } else {
          // do year validation
          let currentYear = new Date().getFullYear();
          let yearDifference = Number(newValue) - currentYear;

          if (Number.isInteger(min) && Number(yearDifference) < min) {
            setValidationError(
              `Cannot be less than ${min} years from the current year.`
            );
            return { success: false };
          }
          if (Number.isInteger(max) && Number(yearDifference) > max) {
            setValidationError(
              `Cannot be more than ${max} years from the current year.`
            );
            return { success: false };
          }
        }
        formData.append("value", newValue);
        break;
      default:
        formData.append("value", newValue);
    }
    return await mutateAsync({ userID, formData });
  }, [
    networkFieldID,
    type,
    mutateAsync,
    userID,
    newValue,
    handleFileUpload,
    min,
    max,
    rangeContext,
  ]);

  return [
    {
      progressPercentage,
      errorMessage,
      clearErrorMessage,
      isLoading: isUploading || isLoading,
    },
    saveField,
  ];
};

const useClearExtendedFieldResponses = ({ networkFieldID, targetUserID }) => {
  return useMutation({
    mutationFn: () =>
      clearExtendedFieldResponse({ networkFieldID, targetUserID }),
  });
};

export { useHandleUploadFile, useClearExtendedFieldResponses, useSaveField };
